import { useEffect, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  CircleNotch,
  EnvelopeSimple,
  Images,
  Package,
  Tray,
  Trophy,
} from "@phosphor-icons/react";
import {
  listSubmissions,
  listProducts,
  listAwards,
  listBrochures,
  listMedia,
} from "@/lib/api";

function parseDate(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).replace(" ", "T"));
  return isNaN(d) ? null : d;
}

function formatDay(iso) {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString("it-IT") : "";
}

function isThisMonth(iso) {
  const d = parseDate(iso);
  if (!d) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export default function DashboardPanel({ onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    // Ogni sezione carica per conto suo: se un endpoint fallisce le altre card restano valide.
    Promise.allSettled([
      listSubmissions(),
      listProducts({ all: true }),
      listAwards({ all: true }),
      listBrochures({ all: true }),
      listMedia(),
    ]).then((results) => {
      if (cancelled) return;
      const [submissions, products, awards, brochures, media] = results.map(
        (r) => (r.status === "fulfilled" ? r.value.items || [] : null)
      );
      const failed = results.find((r) => r.status === "rejected");
      if (failed) setError(failed.reason?.message || "Errore di caricamento");
      setData({ submissions, products, awards, brochures, media });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) {
    return (
      <div className="flex justify-center py-24">
        <CircleNotch size={28} weight="bold" className="animate-spin text-[#EB0D0D]" />
      </div>
    );
  }

  const { submissions, products, awards, brochures, media } = data;
  const count = (list) => (list ? list.length : "—");
  const drafts = (list) => {
    const n = list ? list.filter((x) => !x.is_published).length : 0;
    return n > 0 ? `${n} non pubblicat${n === 1 ? "o" : "i"}` : null;
  };
  const unread = submissions ? submissions.filter((s) => !s.is_read).length : 0;
  const thisMonth = submissions
    ? submissions.filter((s) => isThisMonth(s.created_at)).length
    : 0;

  const stats = [
    {
      key: "richieste",
      label: "Richieste",
      icon: Tray,
      value: count(submissions),
      hint: submissions ? `${thisMonth} questo mese` : null,
      badge: unread > 0 ? `${unread} da leggere` : null,
    },
    { key: "prodotti", label: "Prodotti", icon: Package, value: count(products), hint: drafts(products) },
    { key: "premi", label: "Premi", icon: Trophy, value: count(awards), hint: drafts(awards) },
    { key: "brochure", label: "Brochure", icon: BookOpen, value: count(brochures), hint: drafts(brochures) },
    { key: "media", label: "Media", icon: Images, value: count(media), hint: "file caricati" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium">
          Dashboard
        </h1>
        <p className="text-sm text-[#5C5A55] mt-1">
          Panoramica dei contenuti e delle richieste.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onNavigate(s.key)}
              data-testid={`dashboard-stat-${s.key}`}
              className="group text-left bg-white border border-[#D1CFC7]/60 hover:border-[#1A1918] transition-colors p-6"
            >
              <div className="flex items-start justify-between mb-5">
                <span className="w-10 h-10 inline-flex items-center justify-center bg-[#F0EFEB] text-[#1A1918]">
                  <Icon size={20} weight="regular" />
                </span>
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="text-[#D1CFC7] group-hover:text-[#EB0D0D] transition-colors"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-4xl tracking-tighter font-medium">
                  {s.value}
                </span>
                {s.badge && (
                  <span className="px-2 py-1 bg-[#EB0D0D] text-[#FAF9F6] text-[10px] uppercase tracking-[0.15em] font-bold">
                    {s.badge}
                  </span>
                )}
              </div>
              <div className="text-sm text-[#1A1918] mt-1">{s.label}</div>
              {s.hint && <div className="text-xs text-[#5C5A55] mt-0.5">{s.hint}</div>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ListCard title="Ultime richieste" action="Vedi tutte" onAction={() => onNavigate("richieste")}>
          {!submissions || submissions.length === 0 ? (
            <EmptyRow>Nessuna richiesta ricevuta.</EmptyRow>
          ) : (
            submissions.slice(0, 5).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onNavigate("richieste")}
                className="w-full text-left flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#F0EFEB] transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {!s.is_read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#EB0D0D] flex-shrink-0" />
                    )}
                    <span className="font-medium text-sm truncate">{s.nome}</span>
                  </div>
                  <div className="text-xs text-[#5C5A55] truncate inline-flex items-center gap-1.5 mt-0.5">
                    <EnvelopeSimple size={12} weight="bold" />
                    {s.email}
                  </div>
                </div>
                <span className="text-xs text-[#5C5A55] whitespace-nowrap">
                  {formatDay(s.created_at)}
                </span>
              </button>
            ))
          )}
        </ListCard>

        <ListCard title="Prodotti" action="Vedi tutti" onAction={() => onNavigate("prodotti")}>
          {!products || products.length === 0 ? (
            <EmptyRow>Nessun prodotto inserito.</EmptyRow>
          ) : (
            products.slice(0, 5).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onNavigate("prodotti")}
                className="w-full text-left flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#F0EFEB] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-10 h-10 object-cover flex-shrink-0 bg-[#F0EFEB]"
                    />
                  ) : (
                    <span className="w-10 h-10 bg-[#F0EFEB] flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    {p.subtitle && (
                      <div className="text-xs text-[#5C5A55] truncate mt-0.5">{p.subtitle}</div>
                    )}
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap ${
                    p.is_published
                      ? "bg-[#1A1918] text-[#FAF9F6]"
                      : "bg-[#F0EFEB] text-[#5C5A55]"
                  }`}
                >
                  {p.is_published ? "Pubblicato" : "Bozza"}
                </span>
              </button>
            ))
          )}
        </ListCard>
      </div>
    </div>
  );
}

function ListCard({ title, action, onAction, children }) {
  return (
    <section className="bg-white border border-[#D1CFC7]/60">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#D1CFC7]/60">
        <h2 className="text-xs uppercase tracking-[0.15em] font-bold">{title}</h2>
        <button
          type="button"
          onClick={onAction}
          className="text-xs font-bold text-[#EB0D0D] hover:underline"
        >
          {action}
        </button>
      </div>
      <div className="divide-y divide-[#D1CFC7]/60">{children}</div>
    </section>
  );
}

function EmptyRow({ children }) {
  return <div className="px-6 py-8 text-sm text-[#5C5A55] text-center">{children}</div>;
}
